import { ReactNode } from "react";

import request from "../libs/request";

import { StoreItemType } from "../types/talk";
import { BuildingId } from "../types/building";

export interface TalkCategory {
  lifeCategoryId?: number;
  alias?: string;
  name?: string;
  url?: string;
  lifeCategoryList?: TalkCategory[];
  icon?: ReactNode;
  storeCategoryId?: null;
  storeList?: StoreItemType[] | null;
  managerOnlyRole?: string | null;
}

export const getTalkCategories = (params: Partial<BuildingId>) => {
  return request.get<TalkCategory[]>("pinelife/category/list", { params });
};

export const getTalkListCategoryMenu = (params: Partial<BuildingId>) => {
  return request.get<TalkCategory[]>("pinelife/category/menu", { params });
};

export type PageableRequest = {
  pageSize?: number;
  page?: number;
};

export type Pageable = PageableRequest & {
  total: number;
  sort: string | null;
  offset: number;
  totalPages: number;
};

export type PageableResponse<T> = {
  items: T[];
  pageable: Pageable;
};

export type TalkComment = {
  lifeCommentId?: number;
  creatorId?: number;
  likeCount?: number;
  lifePostId: number;
  createdAt: string;
  deletedAt?: string;
  content: string;
  parentCommentId?: number;
  commentGroup?: number;
  level?: number;
  isHiddenReportedComment?: boolean;
  commentList?: TalkComment[];
  status?: string;
};

export type PostUserItem = {
  fileInfo: { items: ImageFileInfoEachSize[] };
  userId: number;
  userName: string;
  userNickname: string | null;
};

export type LikeItem = {
  lifeCommentId?: number;
  lifePostId?: number;
  likeCount: number;
  userLiked: boolean;
  userList: PostUserItem[];
};

export type TalkListItem = {
  buildingName: string;
  commentCount: number;
  content: string;
  createdAt: string;
  createdBy: number;
  lifeCategoryId: number;
  lifePostId: number;
  likeCount: number;
  modifiedAt: string | null;
  nickname: string;
  shareCount: number;
  status: string;
  title: string;
  useComment: null;
  userName: string;
  viewCount: number;
  postLike: LikeItem;
  categoryNameL: string | null;
  categoryNameM: string | null;
  categoryNameS: string | null;
  fileInfo: { items: ImageFileInfoEachSize[] } | null;
  memberFileInfo: { items: ImageFileInfoEachSize[] } | null;
  userRole: string;
};

export type ImageFileInfoEachSize = {
  fileId: number | null;
  scale: string;
  sortOrder: number;
  url: string;
};

export type TalkDetail = AddNewPostRequest & {
  commentCount: number | null;
  companyId: string | null;
  createdAt: string;
  createdBy: number;
  fileInfo: { items: ImageFileInfoEachSize[] };
  isReportedHidden: boolean;
  isTop: boolean;
  lifePostId: number;
  likeCount: number;
  modifiedAt: string | null;
  nickname: string;
  shareCount: number;
  useComment: boolean;
  userName: string;
  viewCount: number;
  memberFileInfo: { items: ImageFileInfoEachSize[] };
  userRole: string;
};

export type TalkGroupPostItem = {
  alias: string;
  lifeCategoryId: number;
  lifePostList: TalkListItem[];
  name: string;
  url: string;
  managerOnlyRole: string | null;
  storeCategoryId: number | null;
  storeList: null;
};

export const getTalkDetail = (postId: string) => {
  return request.get<TalkDetail>(`pinelife/post/${postId}`);
};

export const getTalkPostLike = (postId: string) =>
  request.get(`pinelife/recodeLike/post/${postId}`);

export const getTalkPostList = (url: string) => {
  return request.get<PageableResponse<TalkListItem>>(
    url?.replace("/v2/app/", "")
  );
};

export const getTalkPostListGroup = (url: string) =>
  request.get<TalkGroupPostItem[]>(url?.replace("/v2/app/", ""));

export type TalkCommentRequest = {
  lifePostId: number;
  content: string;
  parentCommentId?: number;
};

export const postComment = (params: TalkCommentRequest) => {
  return request.post(`pinelife/comment`, params, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};


export type PutCommentRequest = {
  content: string;
  status: string;
};

export const putComment = (lifeCommentId: number, params: PutCommentRequest) => {
  request.put(`pinelife/comment/${lifeCommentId}`, params, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

export const deleteComment = (commentId: number) => {
  return request.delete(`pinelife/comment/${commentId}`);
};

export const deletePost = (postId: number) => {
  return request.delete(`pinelife/post/${postId}`);
};

type RecodeLikeRequest = {
  lifePostId?: number;
  lifeCommentId?: number;
};

export const likeRecode = (params: RecodeLikeRequest) =>
  request.post(`pinelife/recodeLike`, params);

export const unlikePost = (params: RecodeLikeRequest) =>
  request.delete(`pinelife/recodeLike/post/${params.lifePostId}`);

export const unlikeComment = (params: RecodeLikeRequest) =>
  request.delete(`pinelife/recodeLike/comment/${params.lifeCommentId}`);

export type PostCommentRequest = {
  lifePostId: number;
  page?: number;
  pageSize?: number;
};

export type PostComment = LikeItem & {
  // commentLike?: LikeItem;
  commentList?: PostComment[];
  content: string;
  createdAt?: string;
  createdBy: number;
  deletedAt?: string;
  isHiddenReportedComment?: boolean;
  level?: number;
  lifeCommentId?: number;
  lifePostId: number;
  memberBuildingId?: string;
  memberBuildingName?: string;
  memberName?: string;
  memberNickname?: string;
  memberProfileImg: { items: ImageFileInfoEachSize[] };
  parentCommentId?: number;
  status?: string;
  userRole?: string;
};

export const getPostComment = (params: PostCommentRequest) =>
  request.get<PageableResponse<PostComment>>(
    `pinelife/comment/${params.lifePostId}`,
    {
      params,
    }
  );

export type AddNewPostRequest = {
  title: string;
  lifeCategoryId: number;
  content: string;
  status: string;

  useComment?: boolean | null;
  buildingId?: string | null;
  storeId?: string | null;
  isDisplayHotCold?: boolean | null;
};

export const addNewPost = (params: FormData) =>
  request.post(`pinelife/post`, params, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const updatePost = (lifePostId: number, params: FormData) =>
  request.put(`pinelife/post/${lifePostId}`, params, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export type SearchMiddleBuildingId = {
  category: string;
  searchWord?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
};

export type TalkListResponse = {
  items: TalkListItem[];
};

export const searchMiddleCategory = (params: SearchMiddleBuildingId) =>
  request.get<TalkListResponse>("pinelife/post/list/search", {
    params,
  });

export const searchLargeCategory = (params: SearchMiddleBuildingId) =>
  request.get<TalkListResponse>("pinelife/post/list/search/all", {
    params,
  });

export const getManagerNotice = () =>
  request.get<TalkListItem>("pinelife/manager/notice");

export type TalkHotColdItem = BuildingId & {
  lifePostId: number;
  categoryNameL: string;
  categoryNameM: string;
  categoryNameS: string;
  title: string;
  lifeCategoryId: number;
  userName: string;
  nickname: string;
  email: string;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  companyId: string;
  companyName: string;
  buildingName: string;
  isDisplayHotCold: boolean;
  commentCreatedAt: string;
  isVisible: boolean;
};

export const getHotColdNotice = (params: BuildingId) =>
  request.get<TalkHotColdItem>("/pinelife/post/hot-cold", { params });
